import { Box, Paper, TableContainer } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useApi } from "@platform/api";
import { Alert, Typography } from "@platform/design-system";

import { BrokerGroupInfo } from "../../contexts/authContext";
import { submitEvent } from "../../utils/tracking";
import { Loader, UsersTable } from "../../components";
import { useAuth } from "../../hooks";
import { extractErrorMessage } from "../../utils";

import InviteDialog from "./inviteDialog";
import Invites from "./invites";
import { PrepareManageUsersResponse } from "./types";

const manageUsersQueryKey = ["manageUsers"];

const ManageUsers = (): React.JSX.Element => {
  const queryClient = useQueryClient();
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const axiosInstance = useApi();
  const { data, isLoading, error } = useQuery<
    PrepareManageUsersResponse,
    Error
  >({
    queryKey: manageUsersQueryKey,
    queryFn: () =>
      axiosInstance
        .get<PrepareManageUsersResponse>("/brokerportal/v1/prepareManageUsers")
        .then((res) => res.data as PrepareManageUsersResponse),
  });
  const { brokerGroupInfo } = useAuth() as { brokerGroupInfo: BrokerGroupInfo };

  useEffect(() => {
    if (brokerGroupInfo) {
      submitEvent("User Management Page Viewed", {
        broker_name: brokerGroupInfo?.name,
      });
    }
  }, [brokerGroupInfo]);

  const handleCloseInviteForm = (): void => {
    setShowInviteDialog(false);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Box mt={2} mb={2}>
        <Alert severity="error">{extractErrorMessage(error)}</Alert>
      </Box>
    );
  }

  return (
    <div>
      <Invites
        data={data?.invites}
        invalidateQueryKey={manageUsersQueryKey}
        onInvite={() => setShowInviteDialog(true)}
      />
      <Box mt={4}>
        <Box mb={3}>
          <Typography variant="header-medium">Users</Typography>
        </Box>
        <TableContainer component={Paper}>
          <UsersTable data={data?.members} />
        </TableContainer>
      </Box>

      <InviteDialog
        open={showInviteDialog}
        onClose={handleCloseInviteForm}
        brokerId={brokerGroupInfo?.id as number}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: manageUsersQueryKey,
          });
          handleCloseInviteForm();
        }}
      />
    </div>
  );
};

export default ManageUsers;
